import { getGlobalHeadersAsObject } from "../global-headers";

export type Company = {
	id: number;
	created: string;
	updated: string;
	linkedin_id: number;
	linkedin_url?: string;
	url?: string;
	name: string;
	total_ammount_employees: number;
	website: string;
	overview: string;
	industry: string;
	company_size: string;
	company_type: string;
	founded: number;
	specialties: string;
	location: string;
	followers: number;
	ammount_employees_cache: number;
	linkedin_named_id: string;
	cinode_id: number;
	cinode_seo_id: string;
	cinode_url: string;
};

export const getCompanies = (tags: string[], sort?: string): Promise<Company[]> => {
	if (!tags) throw new Error("Called getCompanies without tags supplied.");

	let url = `/api/v2/company/?tags=${tags.join(",")}`;
	if (sort) url += `&ordering=${sort}`;

	return fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => res.json())
		.then((res) => res.results);
};

/** Tricky cache magic stuff. Should probably make a generic function... */
const cachedCompanies = [];
const fetchList = [];

const companyInCache = (id: number) => {
	return cachedCompanies.find((c) => c.id === id);
};

export const getCompanyForId = async (id: number): Promise<Company> => {
	if (!id) throw new Error("Called getCompanyForId without id supplied");

	// Are we already fetching this company?
	if (fetchList.find(([cid]) => cid === id)) {
		return fetchList.find(([cid]) => cid === id)[1];
	}

	// Check if company is already in cache
	if (companyInCache(id)) return companyInCache(id);

	const url = `/api/v2/company/${id}/`;
	const request = fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) =>
			res.json().then((company) => {
				if (res.ok) {
					cachedCompanies.push(company);
					return company;
				}
				return null;
			})
		)
		.catch(() => {
			return null;
		});
	fetchList.push([id, request]);

	return request;
};

/**
 * Fetch multiple companies in a single batched request (much faster than individual calls)
 */
export const getCompaniesForIds = async (ids: number[]): Promise<Company[]> => {
	if (!ids || ids.length === 0) return [];

	const url = `/api/v2/company/?id__in=${ids.join(",")}`;
	const response = await fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		return [];
	}

	const data = await response.json();
	const companies: Company[] = data.results || data;

	// Update cache with fetched companies
	for (const company of companies) {
		if (!companyInCache(company.id)) {
			cachedCompanies.push(company);
		}
	}

	return companies;
};
